import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../../../../../context/LanguageContext';
import { initializeProductSection } from '../script/product-section';
import '../style/product-section.css';

const ProductSection: React.FC = () => {
    const { t, language } = useLanguage();
    const containerRef = useRef<HTMLElement>(null);
    const sortSelectRef = useRef<HTMLSelectElement>(null);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    const translation = t as any;
    const store = translation.STORE;

    useEffect(() => {
        if (!containerRef.current) return;

        const timer = setTimeout(() => {
            initializeProductSection(containerRef.current, sortSelectRef.current, language);
            
            // Dispatch event to notify Firebase script that component is loaded
            const event = new CustomEvent('componentLoaded', {
                detail: { componentName: 'Product Section' }
            });
            window.dispatchEvent(event);
        }, 500);

        return () => {
            clearTimeout(timer);
        };
    }, []);

    // Re-initialize when language changes
    useEffect(() => {
        const timer = setTimeout(() => {
            if (containerRef.current && sortSelectRef.current) {
                initializeProductSection(containerRef.current, sortSelectRef.current, language);
            }
        }, 300);

        return () => {
            clearTimeout(timer);
        };
    }, [language]);

    // Force navbar to sticky mode when sidebar is open
    useEffect(() => {
        if (isSidebarOpen) {
            document.body.classList.add('mobile-menu-visible');
        } else {
            document.body.classList.remove('mobile-menu-visible');
        }
        return () => {
            document.body.classList.remove('mobile-menu-visible');
        };
    }, [isSidebarOpen]);

    // Close sidebar when category is clicked (mobile)
    useEffect(() => {
        const handleCategoryClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            const categoryLink = target.closest('#categoryFilter a, #categoryFilter li');
            
            if (categoryLink && window.innerWidth <= 991) {
                setIsSidebarOpen(false);
            }
        };

        document.addEventListener('click', handleCategoryClick);
        
        return () => {
            document.removeEventListener('click', handleCategoryClick);
        };
    }, []);

    return (
        <section id="product-section-container" className="product" ref={containerRef}>
            <div className="container">
                {/* Mobile Filter Toggle Button */}
                <div className="product__mobile-filter-toggle">
                    <button 
                        type="button"
                        className="product__mobile-filter-btn"
                        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                        aria-label="Toggle filters"
                    >
                        <i className="fa fa-filter"></i>
                        <span>{store.categories}</span>
                    </button>
                </div>

                {/* Mobile Sidebar Overlay */}
                {isSidebarOpen && (
                    <div 
                        className="product__mobile-sidebar-overlay"
                        onClick={() => setIsSidebarOpen(false)}
                    ></div>
                )}

                <div className="row">
                    <div className={`col-xl-3 col-lg-12 product__sidebar-col ${isSidebarOpen ? 'product__sidebar-col--open' : ''}`}>
                        <div className="product__sidebar">
                            <div className="shop-search product__sidebar-single">
                                <form 
                                    action="#"
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        const form = e.currentTarget;
                                        const input = form.querySelector('input[type="text"]') as HTMLInputElement;
                                        if (input) {
                                            // Trigger search by dispatching input event
                                            const inputEvent = new Event('input', { bubbles: true });
                                            input.dispatchEvent(inputEvent);
                                        }
                                        // Close sidebar on mobile after search
                                        if (window.innerWidth <= 991) {
                                            setIsSidebarOpen(false);
                                        }
                                    }}
                                >
                                    <input 
                                        type="text" 
                                        placeholder={store.search}
                                    />
                                    <button type="submit">
                                        <i className="fa fa-search"></i>
                                    </button>
                                </form>
                            </div>
                            <div className="product__categories-box">
                                <div className="product__categories-header">
                                    <h3 className="product__categories-title">
                                        {store.categories}
                                    </h3>
                                    {/* Mobile Close Button */}
                                    <button 
                                        type="button"
                                        className="product__sidebar-close"
                                        onClick={() => setIsSidebarOpen(false)}
                                        aria-label="Close filters"
                                    >
                                        <i className="fa fa-times"></i>
                                    </button>
                                </div>
                                <ul className="product__categories-list list-unstyled" id="categoryFilter">
                                    <li className="active">
                                        <a href="#" data-category="all">
                                            {store.allCategories}
                                        </a>
                                    </li>
                                    {/* Categories will be populated dynamically by JavaScript */}
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div className="col-xl-9 col-lg-12">
                        <div className="product__items">
                            <div className="row">
                                <div className="col-xl-12">
                                    <div className="product__showing-result">
                                        <div className="product__showing-text-box">
                                            <p className="product__showing-text">
                                                {store.loadingProducts}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="product__all">
                                <div className="product__all-tab">
                                    {/* Start Tabs Content Box */}
                                    <div className="tabs-content-box">
                                        {/* Start Tab - Grid */}
                                        <div className="tab-content-box-item" id="grid">
                                            <div className="product__all-tab-content-box-item">
                                                <div className="product__all-tab-single">
                                                    <div className="row">
                                                        {/* Products will be loaded dynamically from Firebase */}
                                                        <div className="col-12 text-center">
                                                            <div className="spinner-border text-primary" role="status">
                                                                <span className="visually-hidden">
                                                                    {store.loadingProducts}
                                                                </span>
                                                            </div>
                                                            <p className="mt-2">
                                                                {store.loadingProducts}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        {/* End Tab - Grid */}
                                        
                                        {/* Start Tab - List */}
                                        <div className="tab-content-box-item tab-content-box-item-active" id="list">
                                            <div className="product__all-tab-content-box-item">
                                                <div className="product__all-tab-single">
                                                    <div className="row">
                                                        {/* Products will be loaded dynamically from Firebase */}
                                                        <div className="col-12 text-center">
                                                            <div className="spinner-border text-primary" role="status">
                                                                <span className="visually-hidden">
                                                                    {store.loadingProducts}
                                                                </span>
                                                            </div>
                                                            <p className="mt-2">
                                                                {store.loadingProducts}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        {/* End Tab - List */}
                                    </div>
                                    {/* End Tabs Content Box */}
                                </div>
                                
                                <ul className="styled-pagination text-center clearfix list-unstyled">
                                    {/* Pagination will be dynamically populated by store-firebase.js */}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ProductSection;

